
import { Component, Prop } from 'vue-property-decorator'
import BaseImage from '@/components/common/BaseImage.vue'
import ContentScroll from '@/components/common/ContentScroll.vue'
import { OptionValidator } from '@/mixins/OptionValidator'
import { DropdownExtension } from '@/mixins/DropdownExtension'
import { OptionApiModel } from '@/interfaces/OptionApiModel.js'
import { SubOptionType } from '@/interfaces/SubOptionType'
import lodash from 'lodash'
import { mixins } from 'vue-class-component'
import { OptionSelectionMixin } from '@/mixins/OptionSelectionMixin'

@Component({
  components: {
    BaseImage,
    ContentScroll
  }
})
export default class OptionItems extends mixins(OptionValidator, DropdownExtension, OptionSelectionMixin) {
  @Prop({ default: [] }) readonly optionList!: Array<OptionApiModel>
  @Prop({ default: [] }) readonly subOptionTypeList!: Array<SubOptionType>
  @Prop() readonly imagePath!: string
  @Prop() readonly showOtherOptions!: boolean

  public optionImageUrl(optionItem: OptionApiModel): string {
    return (optionItem.thumbnailUrl !== null && optionItem.thumbnailUrl !== '' ? optionItem.thumbnailUrl : this.imagePath + optionItem.code + '_th.jpg')
  }

  public optionDescription(optionCode: string): string | undefined {
    return (lodash.find(this.optionList, { code: optionCode }) as OptionApiModel)?.details
  }

  created() {
    this.adjustPosition()
  }

  public get mergedOptionList() {
    const visibleOptions = this.optionList.filter(optionItem => !this.hideOption(optionItem))

    // Group option variants and mark selected one as visible
    lodash(visibleOptions.filter(oi => !!oi.variantSpec))
      .groupBy(v => v.variantSpec?.group)
      .forEach(g => {
        if (g.length === 1) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          g[0].variantSpec!.isVisible = true
          return
        }

        g.forEach(oi => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          oi.variantSpec!.isVisible = false
          oi.variants = g
        })

        let visibleIndex = 0
        const valueInModel = this.model.configurator[g[0].optionType.modelProperty]

        const selectedVariant = g.findIndex(v => v.code === valueInModel)
        if (selectedVariant >= 0) { visibleIndex = selectedVariant }

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        g[visibleIndex].variantSpec!.isVisible = true
      })

    return visibleOptions.filter(oi => !oi.variantSpec || oi.variantSpec.isVisible)
  }

  optionName(optionItem: OptionApiModel): any {
    return optionItem.variantSpec?.group ?? optionItem.name
  }

  optionKey(optionItem: OptionApiModel): any {
    return optionItem.variantSpec?.group ?? optionItem.id
  }

  optionClicked($event: MouseEvent, variant: OptionApiModel) {
    $event.stopPropagation()

    if (!this.isOptionSelected(variant) && this.disableOption(variant)) { return }

    this.$emit('click-option', variant)
  }

  optionClass(optionItem: OptionApiModel) {
    const classes = {} as any
    classes.active = this.isOptionSelected(optionItem)
    classes.disabled = this.isOptionDisabled(optionItem)

    const optionTypeClass = 'option-type-' + optionItem.optionType.modelProperty
    classes[optionTypeClass] = true
    return classes
  }

  get disableAll() {
    return !this.optionList.some(option => this.isOptionSelected(option))
  }

  get showDescription() {
    return this.model.configurator.countertop === 'NONE' && !this.disableAll
  }

  get showSuboptions() {
    return !this.showDescription
  }

  public isOptionDisabled(optionItem: OptionApiModel) {
    if (optionItem.variants && optionItem.variants.length > 1) {
      return !optionItem.variants.some(v => !this.disableOption(v))
    } else {
      return this.disableOption(optionItem)
    }
  }

  public disableDescription(optionItem: OptionApiModel) {
    if (optionItem.variants && optionItem.variants.length > 1) {
      const allDisabled = !optionItem.variants.some(v => !this.disableOption(v))
      if (allDisabled) { return this.optionTooltipDescription(optionItem) }
    } else {
      return this.optionTooltipDescription(optionItem)
    }

    return undefined
  }

  public isVariantDisabled(optionItem: OptionApiModel) {
    return this.disableOption(optionItem)
  }

  public disableVariantDescription(optionItem: OptionApiModel) {
    return this.optionTooltipDescription(optionItem)
  }

  public getSelectedSubOptionName(subOptionType: SubOptionType) {
    const modelValue = this.model.configurator[subOptionType.modelProperty]
    return subOptionType.subOptions.find(s => s.code === modelValue)?.name ?? 'Select'
  }

  public getOptionReactiveKey(optionItem: OptionApiModel) {
    // Create a reactive key that includes the disabled state to force re-render
    const baseKey = this.optionKey(optionItem)
    const isDisabled = this.isOptionDisabled(optionItem)
    const description = this.optionTooltipDescription(optionItem)?.length ?? 0
    return `${baseKey}-${isDisabled}-${description}`
  }
}
